import type { ReclassCriteria } from '../Reclassification/reclassCriteria';
import type { WidaYearRecord } from '../Upload/widaHistory';
import type { SisStudent } from '../Upload/sisRoster';
import { computeClassificationTenure } from '../../core/classificationTenure';
import { DEFAULT_THRESHOLDS } from '../../core/thresholds';
import { deriveAccommodationInputs } from '../IndividualPlan/accommodations';
import { DOMAINS, type DomainKey, type PlanStudent } from '../IndividualPlan/derive';
import { deriveFocusByDomainInputs, type FocusByDomainContent } from '../IndividualPlan/domainFocus';
import type { FlashlightAssignmentsResponse, FlashlightBenchmarksResponse } from '../IndividualPlan/flashlightBenchmarks';
import type { SavedPlanBody } from '../IndividualPlan/planCache';
import { derivePlanSnapshotInputs } from '../IndividualPlan/planSnapshotContent';

export const SAMPLE_STUDENT_ID = 'sample-layla-hasan';
export const SAMPLE_SCHOOL = 'Cedar Valley High School';
const SAMPLE_DISTRICT = 'Cedar Valley Unified School District';

type Levels = { l: number; s: number; r: number; w: number; c: number };
type Scales = Levels;

function year(
  endYear: number,
  grade: string,
  school: string,
  yearsInProgram: number,
  level: Levels,
  scale: Scales,
): WidaYearRecord {
  return {
    year: endYear,
    grade,
    school,
    tier: 'B',
    testDate: `02/10/${endYear}`,
    waived: false,
    yearsInProgram,
    listening: { scale: scale.l, level: level.l },
    speaking: { scale: scale.s, level: level.s },
    reading: { scale: scale.r, level: level.r },
    writing: { scale: scale.w, level: level.w },
    composite: { scale: scale.c, level: level.c },
    assessmentScale: 'access',
  };
}

const MIDDLE = 'Cedar Valley Middle School';

const sampleSis: SisStudent = {
  studentId: SAMPLE_STUDENT_ID,
  altIds: [],
  school: SAMPLE_SCHOOL,
  grade: '10',
  serviceStartYear: 2018,
  yearsInEl: 8,
  elStatus: 'Active',
  programModel: 'Sheltered Language Immersion',
  homeLanguage: 'Arabic',
  elTeacher: 'Mr. Rogers',
  pronouns: null,
  originatingDistrict: null,
  exitDate: null,
  exitReason: null,
  newcomerFlag: false,
  duallyIdentified: false,
  alternateAccess: false,
};

export const sampleStudent: PlanStudent = {
  id: SAMPLE_STUDENT_ID,
  stateStudentId: '',
  localStudentId: '',
  firstName: 'Layla',
  middleName: 'N.',
  lastName: 'Hasan',
  fullName: 'Layla N. Hasan',
  nativeLanguage: 'Arabic',
  district: SAMPLE_DISTRICT,
  districtState: 'UT',
  latestGrade: '10',
  latestSchool: SAMPLE_SCHOOL,
  yearsInEl: 8,
  tenure: computeClassificationTenure({
    serviceStartYear: 2019,
    yearsInEl: 8,
    startedInDistrict: true,
    newcomerFlag: false,
    earliestWidaYear: 2022,
    currentYear: 2026,
    thresholds: DEFAULT_THRESHOLDS,
  }),
  years: [
    year(2022, '6', MIDDLE, 4, { l: 2.6, s: 2.8, r: 2.4, w: 2.2, c: 2.4 }, { l: 311, s: 318, r: 299, w: 290, c: 302 }),
    year(2023, '7', MIDDLE, 5, { l: 3.3, s: 3.5, r: 2.9, w: 2.6, c: 3.0 }, { l: 338, s: 345, r: 318, w: 305, c: 325 }),
    year(2024, '8', MIDDLE, 6, { l: 3.8, s: 4.0, r: 3.4, w: 3.0, c: 3.5 }, { l: 356, s: 363, r: 338, w: 320, c: 345 }),
    year(2025, '9', SAMPLE_SCHOOL, 7, { l: 4.3, s: 4.5, r: 3.5, w: 3.3, c: 3.9 }, { l: 372, s: 380, r: 342, w: 332, c: 360 }),
    year(2026, '10', SAMPLE_SCHOOL, 8, { l: 4.5, s: 4.7, r: 3.6, w: 3.1, c: 4.1 }, { l: 452, s: 461, r: 410, w: 395, c: 428 }),
  ],
  sis: sampleSis,
};

const BENCHMARK_SCORES = { boy: 3.5, moy: 4.0, eoy: null };
const BENCHMARK_AVERAGE = { boy: 3.5, moy: 3.8, eoy: null };

export const sampleBenchmarks: FlashlightBenchmarksResponse = {
  hasData: true,
  years: [
    {
      schoolYear: 2026,
      benchmarks: [
        {
          domain: 'speaking',
          indicators: ['Interpretation', 'Vocabulary', 'Grammar', 'Fluency', 'Pronunciation'].map((name) => ({
            name,
            scores: BENCHMARK_SCORES,
          })),
          average: BENCHMARK_AVERAGE,
        },
        {
          domain: 'writing',
          indicators: ['Interpretation', 'Vocabulary', 'Grammar'].map((name) => ({
            name,
            scores: BENCHMARK_SCORES,
          })),
          average: BENCHMARK_AVERAGE,
        },
      ],
    },
  ],
  sources: [{ database: 'sample', status: 'ok' }],
};

const ASSIGNMENTS: Array<[string, string]> = [
  ['Global Citizenship', '2026-03-30T16:00:00Z'],
  ['Cultural Diversity', '2026-01-19T16:00:00Z'],
  ['Ecosystems', '2025-11-24T16:00:00Z'],
  ['Climate Change', '2025-09-29T16:00:00Z'],
  ['Peer Interview Project', '2025-09-22T16:00:00Z'],
  ['Debate: Renewable Energy', '2025-09-15T16:00:00Z'],
  ['Oral Presentation: My Home Country', '2025-09-08T16:00:00Z'],
  ['Family Traditions', '2025-09-02T16:00:00Z'],
];

function assignments(domain: 'speaking' | 'writing', subDomains: string[]) {
  return {
    domain,
    assignments: ASSIGNMENTS.map(([name, date], i) => ({
      assignmentId: `${domain}-${i + 1}`,
      name,
      date,
      subDomain: subDomains[i] ?? subDomains[0] ?? '',
      score: 4,
    })),
  };
}

export const sampleAssignments: FlashlightAssignmentsResponse = {
  hasData: true,
  years: [
    {
      schoolYear: 2026,
      assignments: [
        assignments('speaking', ['Fluency', 'Fluency', 'Pronunciation', 'Fluency', 'Grammar', 'Pronunciation', 'Fluency', 'Fluency']),
        assignments('writing', ['Grammar', 'Interpretation', 'Vocabulary', 'Vocabulary', 'Interpretation', 'Interpretation', 'Vocabulary', 'Interpretation']),
      ],
    },
  ],
  sources: [{ database: 'sample', status: 'ok' }],
};

const PLAN_SNAPSHOT = {
  capabilities: [
    'Listening: Can follow grade-level classroom discussion and identify implied meaning with limited support (Expanding).',
    'Speaking: Can sustain extended academic discussion with precise vocabulary in familiar content (Expanding).',
    'Reading: Can read academic texts on familiar topics and identify main ideas with developing independence (Developing).',
    'Writing: Can produce connected paragraphs on familiar topics with developing organization (Developing).',
  ],
  areasForDevelopment: [
    'Reading: Working toward reading grade-level academic texts on unfamiliar topics with developing independence (currently Developing).',
    'Writing: Working toward producing multi-paragraph academic writing with varied sentence structures (currently Developing).',
    'Listening: Working toward following grade-level classroom discussion on unfamiliar academic content without visual support (currently Expanding).',
  ],
};

const SUGGESTED_SUPPORTS = [
  'Coordinate with content-area teachers so academic vocabulary instruction is consistent across classes: shared word lists, shared definitions, shared expectations.',
  'Allow extended wait time (10+ sec) before calling on the student verbally.',
  'Pre-teach academic vocabulary with student-friendly definitions and visual supports before introducing new content.',
  'Use graphic organizers to help Layla structure ideas before producing extended written or spoken responses.',
];

const FOCUS_BY_DOMAIN: Record<DomainKey, FocusByDomainContent> = {
  listening: {
    recommendation:
      'Build listening stamina with extended academic audio and video on unfamiliar content: short lectures, podcasts and recorded discussions followed by structured note-taking and partner summaries. Reduce visual scaffolds gradually so Layla practices following implied meaning from the language alone.',
    accommodations: [
      'Preview key vocabulary and the structure of a talk before Layla listens to it.',
      'Pair audio with guided notes that fade out over the term as independence grows.',
    ],
  },
  speaking: {
    recommendation:
      'Keep Speaking strong with regular extended academic talk: Socratic seminars, structured debates and oral presentations that require precise, content-specific vocabulary. Layla has cleared the 3.5 Speaking minimum, so the aim is maintaining range while the other domains catch up.',
    accommodations: [
      'Give sentence frames for academic argument and rebuttal, then withdraw them as Layla uses the structures on her own.',
      'Schedule at least one extended oral response per week and give feedback on precision of vocabulary.',
    ],
  },
  reading: {
    recommendation:
      'Focus on academic-language reading across content areas: paired text comparisons, argument analysis, and structured discussion of grade-level texts. Coordinate with content-area teachers on shared vocabulary and writing expectations.',
    accommodations: [
      'Provide academic vocabulary lists with student-friendly definitions before each reading lesson.',
      'Use partner reading with grade-level texts paired with text-dependent questions that target inference and author’s purpose.',
    ],
  },
  writing: {
    recommendation:
      'Prioritize multi-paragraph academic writing with varied sentence structures: planned essays with explicit organization, revision cycles focused on cohesion, and weekly short responses that reuse the vocabulary from Reading. Writing fell 0.2 this year and is now the domain furthest from the exit criteria.',
    accommodations: [
      'Use graphic organizers and paragraph frames before drafting, then remove them as structure becomes routine.',
      'Give targeted feedback on sentence variety and transitions rather than on every error at once.',
    ],
  },
};

export function sampleSavedPlan(criteria: ReclassCriteria): SavedPlanBody {
  const focusInputs: Record<string, unknown> = {};
  for (const domain of DOMAINS) {
    focusInputs[domain] = deriveFocusByDomainInputs(sampleStudent, domain, criteria);
  }
  return {
    focusByDomain: FOCUS_BY_DOMAIN,
    accommodations: SUGGESTED_SUPPORTS,
    planSnapshot: PLAN_SNAPSHOT,
    inputs: {
      focusByDomain: focusInputs,
      accommodations: deriveAccommodationInputs(sampleStudent),
      planSnapshot: derivePlanSnapshotInputs(sampleStudent, criteria),
    },
  };
}
